import type { FactorsDto, FactorsMarketDto } from "@eu.mdotm.api/sphere";
import GraphLegend from "$root/components/GraphLegend";
import { IconWalls } from "$root/components/IconWall";
import InfiniteLoader from "$root/components/InfiniteLoader";
import ColoredRectangle from "$root/components/icons/ColoredRectangle";
import { useEventBus } from "$root/event-bus";
import { outlookRepository } from "$root/functional-areas/outlook/repository";
import useOutlookComposition from "$root/hooks/useOutlookComposition";
import { Intercom } from "$root/third-party-integrations/initIntercom";
import { BarGraphPCSvg } from "$root/ui-lib/charts";
import { commonColumns } from "$root/ui-lib/common-table-columns";
import {
	type ContextContent,
	customObjectEntriesFn,
	customObjectValuesFn,
	getGraphMarkers,
	roundCustomByStep,
	withContext,
} from "$root/utils";
import { useMappedQuery } from "$root/third-party-integrations/react-query/hooks";
import { UniverseFiltersContext } from "$root/widgets-architecture/contexts/filters";
import { useWidgetOptions } from "$root/widgets-architecture/layout/WidgetsMapper/context";
import {
	type TableColumn,
	ActionText,
	AutoTooltip,
	BaseTable,
	Row,
	TableDataCell,
	Text,
	TooltipContent,
} from "@mdotm/shadcn/legacy/components";
import { ForEach } from "@mdotm/utils/react-extra";
import { builtInSort, noop } from "@mdotm/shadcn/legacy/utils";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Switch } from "@mdotm/shadcn/mdotm/components";

type Unpacked<T> = T extends (infer U)[] ? U : T;

type ReducedResultProps = {
	[key: string]: {
		[key in keyof FactorsMarketDto]?: FactorsDto;
	} & { groupKey: string };
};

const FactorsExposure = (props: ContextContent<typeof UniverseFiltersContext>) => {
	const { firstFilter, secondFilter, thirdFilter } = props;
	const { assetClass, geography, sector } = useOutlookComposition(firstFilter, secondFilter, thirdFilter);
	const [filterBySignificant, setFilterBySignificant] = useState(true);
	const { t } = useTranslation();

	const FactorsDescription = useMemo(
		() =>
			t("EXPOSUREFACTORS.TOOLTIPDESCRIPTIONS", {
				returnObjects: true,
			}),
		[t],
	);

	const factorsByAssetClassQuery = outlookRepository.factorsByAssetClass.useQuery({
		assetClass,
		geography,
		sector,
	});

	// UseQuery
	const {
		data: FactorsData,
		isLoading,
		isError,
		refetch,
	} = useMappedQuery(
		(rawData) => {
			const aggregateData = customObjectValuesFn(
				customObjectEntriesFn(rawData).reduce<ReducedResultProps>((acc, [key, factorList]) => {
					factorList?.forEach((factor) => {
						if (factor.key === undefined) {
							acc.undefinedKey = {
								...acc.undefinedKey,
								[key]: factor,
								groupKey: "undefinedKey",
							};
						} else {
							acc[factor.key] = {
								...acc[factor.key],
								[key]: factor,
								groupKey: factor.key,
							};
						}
					});
					return acc;
				}, {}),
			);

			return aggregateData;
		},
		[],
		factorsByAssetClassQuery,
	);

	useEventBus("market-update", () => {
		refetch().catch(noop);
	});

	const calculatedData = useMemo(() => {
		const filteredData = filterBySignificant
			? FactorsData?.filter(({ market }) => Math.abs(market?.value ?? 0) >= 0.01)
			: FactorsData;
		if (!filteredData) {
			return filteredData;
		}
		return [...filteredData].sort(
			(a, b) => builtInSort(Math.abs(a.market?.value ?? 0), Math.abs(b.market?.value ?? 0)) * -1,
		);
	}, [FactorsData, filterBySignificant]);

	const graphLimits = useMemo(() => {
		const limits = (calculatedData ?? []).reduce(
			(a, c) => {
				return {
					maxValue: Math.max(a.maxValue, c.market?.value ?? 0),
					minValue: Math.min(a.minValue, c.market?.value ?? 0),
				};
			},
			{ maxValue: 0, minValue: 0 },
		);
		return {
			maxValue: roundCustomByStep(limits.maxValue, 0.5),
			minValue: roundCustomByStep(limits.minValue, 0.5),
		};
	}, [calculatedData]);

	const graphColumnMarkers = useMemo(() => {
		const markers = getGraphMarkers(graphLimits.maxValue, graphLimits.minValue, 0.5, "", 10);
		return markers;
	}, [graphLimits.maxValue, graphLimits.minValue]);

	const columns = useMemo<Array<TableColumn<Unpacked<NonNullable<typeof calculatedData>>>>>(
		() => [
			{
				minWidth: 244,
				header: t("FACTORSEXPOSURE.TABLE.FACTORS"),
				cellClassName: "underline",
				content: ({ market }, cellProps) => (
					<AutoTooltip
						severity="info"
						trigger={({ innerRef }) => (
							<TableDataCell {...cellProps}>
								<strong ref={innerRef}>{market?.label}</strong>
							</TableDataCell>
						)}
						position="bottom"
						align="startToEnd"
					>
						<TooltipContent>
							<Text type="Body/M/Book">
								{FactorsDescription[market?.key as keyof typeof FactorsDescription] ?? market?.key} &nbsp;
							</Text>
							<ActionText type="Body/S/Book" onClick={() => Intercom.showArticle("marketRegimeAnalysysTool")}>
								Read more
							</ActionText>
						</TooltipContent>
					</AutoTooltip>
				),
				sortFn: (a, b) => builtInSort(a.market?.label, b.market?.label),
				name: "factors",
			},
			{
				...commonColumns.float(["market", "value"]),
				width: 112,
				header: t("FACTORSEXPOSURE.TABLE.MARKET"),
				sortFn: (a, b) => builtInSort(Math.abs(a.market?.value ?? 0), Math.abs(b.market?.value ?? 0)),
				name: "market",
			},
			{
				name: "graph",
				minWidth: 300,
				header: (headerProps) => (
					<Row {...headerProps} justifyContent="space-between" alignItems="center">
						<ForEach collection={graphColumnMarkers}>
							{({ label }) => (
								<Text as="div" type="Body/S/Book">
									{label}
								</Text>
							)}
						</ForEach>
					</Row>
				),
				content: ({ market }, cellProps) => (
					<Row justifyContent="center" {...cellProps} alignItems="center">
						<BarGraphPCSvg
							className="w-full"
							options={{
								resize: true,
								marksLabels: false,
								markerStep: 0.5,
								scale: { max: graphLimits.maxValue, min: graphLimits.minValue },
							}}
							data={[{ value: market?.value ?? 0, color: market?.relevant ? "#005C8B" : "red" }]}
						/>
					</Row>
				),
			},
		],
		[FactorsDescription, graphColumnMarkers, graphLimits.maxValue, graphLimits.minValue, t],
	);

	useWidgetOptions(
		() => ({
			title: t("FACTORSEXPOSURE.TITLE"),
			tooltip: (
				<TooltipContent>
					<Text type="Body/M/Book">
						Gain insights into the performance drivers of the asset class through factor exposure analysis. Discover how
						Sphere calculates its factors by accessing more information &nbsp;
					</Text>
					<ActionText type="Body/S/Book" onClick={() => Intercom.showArticle("marketRegimeAnalysysTool")}>
						here.
					</ActionText>
				</TooltipContent>
			),
		}),
		[t],
	);

	return isLoading ? (
		<InfiniteLoader />
	) : isError || !calculatedData ? (
		<IconWalls.ErrorData />
	) : calculatedData.length === 0 ? (
		<IconWalls.DataNotAvailable />
	) : (
		<>
			<div className="flex justify-end">
				<Switch onChange={() => setFilterBySignificant(!filterBySignificant)} checked={filterBySignificant}>
					Significant values only
				</Switch>
			</div>
			<BaseTable columns={columns} rows={calculatedData} />
			<GraphLegend>
				<div className="legend-container light book" style={{ marginRight: "1rem" }}>
					<ColoredRectangle color="#005C8B" variant="vertical" /> Market
				</div>
			</GraphLegend>
		</>
	);
};

export default withContext(UniverseFiltersContext)(FactorsExposure);
